import {
    IVanillaReaderPublicationStyleOptions,
    IVanillaReaderTtsOptions,
    IVanillaReaderUiColorTheme, IVanillaReaderViewOptions,
    VanillaReaderRendererNames,
} from '../VanillaReader/VanillaReaderModel';
import {Key} from "../utils/Key.enum";
import {IVanillaReaderAppEvent_NAVIGATION_INTENT} from '../VanillaReaderEventBus/IVanillaReaderAppEventTypeMap';

/*
* # IVanillaReaderAppUiSettingsData
*
* ## RESPONSIBILITIES
*
* This interface defines the data that is needed by the application UI to restore its settings from a previous setting.
*
* It is used as a schema in the `IVanillaReaderDataStore` implementations so change this type with caution if offer the
* app as a PWA as it will require migration.
*
* ## WARNING ⚠️
*
* TYPE IS USED IN STORE. CHANGES WILL NEED DB MIGRATION
*
**/
export interface IVanillaReaderUiOptionsData {
    _id?: number,
    dateCreated?: number;
    showNarrationControls?: boolean;
    ignoreAspectRatio?: boolean;
    useReducedMotion?: boolean;
    useDarkMode?: boolean;
    activeRendererTypeName?: VanillaReaderRendererNames;
    activeUiPaletteName?: string,
    activePublicationPaletteName?: string;
    activePublicationFontSetName?: string;
    publicationStyleOptions?: IVanillaReaderPublicationStyleOptions;
    mediaPlayerPlaybackRate?: number;
    ttsOptions?: IVanillaReaderTtsOptions,
    useAccessibilityMode?: boolean;
    uIActionKeyCommands?: IVanillaReaderUiActionKeyCommand[],
    showReadingRuler?: boolean;
    readingRulerOptions?: IVanillaReaderUiReadingRulerOptions;
    showPageNumbers?: boolean;
    scrollOverflowingTables?: boolean;
}

/**
 * # VanillaReaderUiActionNames
 *
 * ## RESPONSIBILITIES
 *
 * This enum holds all the UI actions that can be performed using keyboard shortcuts and through the UIActions menu.
 *
 * */
export enum VanillaReaderUiActionNames {
    ACTIONS_MENU_OPEN = 'uiaction_actions_menu_open',
    MAIN_MENU_OPEN = 'uiaction_main_menu_open',
    WHERE_AM_I = 'uiaction_where_am_i',
    TOC_OPEN_DIALOG= 'uiaction_toc_open',
    NAVIGATE_NEXT= 'uiaction_navigate_next',
    NAVIGATE_PREVIOUS= 'uiaction_navigate_previous',
    NAVIGATE_NEXT_HEADING= 'uiaction_navigate_next_heading',
    NAVIGATE_PREVIOUS_HEADING= 'uiaction_navigate_previous_heading',
    HIGHLIGHT_OPEN_DIALOG_ALL = 'uiaction_highlight_open_dialog_all',
    HIGHLIGHT_OPEN_DIALOG_ACTIVE = 'uiaction_highlight_open_dialog_active',
    HIGHLIGHT_SELECTION = 'uiaction_highlight_selection',
    HIGHLIGHT_CLIPBOARD = 'uiaction_highlight_clipboard',
    HIGHLIGHT_READING_POSITION = 'uiaction_highlight_reading_position',
    HIGHLIGHT_ACTIVE_CONTENT = 'uiaction_highlight_active_content',
    BOOKMARK_SELECTION = 'uiaction_bookmark_selection',
    BOOKMARK_CLIPBOARD = 'uiaction_bookmark_clipboard',
    BOOKMARK_READING_POSITION = 'uiaction_bookmark_reading_position',
    BOOKMARK_OPEN_DIALOG_ALL = 'uiaction_bookmark_open_dialog_all',
    BOOKMARK_OPEN_DIALOG_ACTIVE = 'uiaction_bookmark_open_dialog_active',
    SEARCH_DIALOG_OPEN = 'uiaction_search_open_dialog_active',
    SEARCH_CLEAR = 'uiaction_search_clear',
    MEDIA_PLAYBACK_START = 'uiaction_media_playback_start',
    MEDIA_PLAYBACK_STOP = 'uiaction_media_playback_stop',
    MEDIA_PLAYBACK_SKIP_FORWARD = 'uiaction_media_playback_skip_forward',
    MEDIA_PLAYBACK_SKIP_BACKWARD = 'uiaction_media_playback_skip_backward',
    MEDIA_PLAYBACK_ESCAPE_ACTIVE_SEGMENT = 'uiaction_media_playback_escape_segment',
    MEDIA_PLAYBACK_ESCAPE_ALL = 'uiaction_media_playback_escape_all',
    HELP_DIALOG_OPEN = 'uiaction_help_dialog_open',
    ACCESSIBILITY_MODE_ENTER = 'uiaction_accessibility_mode_enter',
    ACCESSIBILITY_MODE_EXIT = 'uiaction_accessibility_mode_exit',
    SETTINGS_DIALOG_OPEN = 'uiaction_settings_dialog_open',
    SETTINGS_RESET = 'uiaction_settings_reset',
    BOOKSHELF_DIALOG_OPEN = 'uiaction_bookshelf_dialog_open',
    CLOSE_DIALOGS_AND_FOCUS_ON_READING_POSITION = 'uiaction_close_dialogs_and_focus_on_reading_position',
    GOTO_PAGE_DIALOG_OPEN = 'uiaction_goto_page_dialog_open',
    ZOOM_TOOL_ACTIVATE = 'uiaction_zoom_mode_enter',
    ZOOM_TOOL_DEACTIVATE = 'uiaction_zoom_mode_exit',
    ZOOM_OUT = 'uiaction_zoom_out',
    ZOOM_IN = 'uiaction_zoom_in',
    ZOOM_RESET = 'uiaction_zoom_reset',
    READING_RULER_ACTIVATE = 'uiaction_ruler_activate',
    READING_RULER_DEACTIVATE = 'uiaction_ruler_deactivate',
    READING_RULER_MOVE_NEXT = 'uiaction_ruler_move_next',
    READING_RULER_MOVE_PREVIOUS = 'uiaction_ruler_move_previous',
    LANDMARKS_DIALOG_OPEN = 'uiaction_landmarks_dialog_open',
    NAVIGATION_HISTORY_DIALOG_OPEN = 'uiaction_navigation_history_dialog_open',
}

export const enum CssMixBlendModeValues {
    INHERIT = 'inherit',
    INITIAL = 'initial',
    REVERT = 'revert',
    REVERT_LAYER = 'revert-layer',
    UNSET = 'unset',
    NORMAL = 'normal',
    MULTIPLY = 'multiply',
    SCREEN =  'screen',
    OVERLAY = 'overlay',
    DARKEN = 'darken',
    LIGHTEN = 'lighten',
    COLOR_DODGE = 'color-dodge',
    COLOR_BURN = 'color-burn',
    HARD_LIGHT = 'hard-light',
    SOFT_LIGHT = 'soft-light',
    DIFFERENCE = 'difference',
    EXCLUSION = 'exclusion',
    HUE = 'hue',
    SATURATION = 'saturation',
    COLOR = 'color',
    LUMINOSITY = 'luminosity',
}

export interface IVanillaReaderUiAction {
    name: VanillaReaderUiActionNames;
}

export interface IVanillaReaderUiActionKeyCommand {
    actionName: VanillaReaderUiActionNames;
    key: string;
    modifiers: Key[];
}

export interface IVanillaReaderUiReadingRulerOptions {
    overlayIsVisible?: boolean,
    overlayBackgroundColor?: string,
    overlayOpacity?: number,
    overlayMixBlendMode?: CssMixBlendModeValues,

    rulerBackgroundColor?: string,
    rulerOpacity?: number,
    rulerMixBlendMode?: CssMixBlendModeValues,
    rulerMinHeight?: number,

    rulerDragThreshold?: number,

    rulerBorderTopWidth?: number,
    rulerBorderBottomWidth?: number,
    rulerBorderLeftWidth?: number,
    rulerBorderRightWidth?: number,
    rulerBorderTopColor?: string,
    rulerBorderBottomColor?: string,
    rulerBorderLeftColor?: string,
    rulerBorderRightColor?: string,

    lineBoxBackgroundColor?: string,
    lineBoxOpacity?: number,
    lineBoxMixBlendMode?: CssMixBlendModeValues,
    lineBoxLineVerticalSpacing?: number,

    lineBoxBorderTopWidth?: number,
    lineBoxBorderBottomWidth?: number,
    lineBoxBorderLeftWidth?: number,
    lineBoxBorderRightWidth?: number,
    lineBoxBorderTopColor?: string,
    lineBoxBorderBottomColor?: string,
    lineBoxBorderLeftColor?: string,
    lineBoxBorderRightColor?: string,

    lineBoxMinHeight?: number,
    lineBoxMaxHeight?: number,
    lineBoxLineSpan?: number,

    snapToLineBoxes?: boolean,
}

export enum VanillaReaderUiOptionsPresetNames {
    GUIDED_READING_MODE = 'GUIDED_READING_MODE',
    AT_READING_MODE = 'AT_READING_MODE',
    READING_RULER_MODE = 'READING_RULER_MODE',
}

export interface VanillaReaderUiActiveOptionsPresetData {
    presetName: VanillaReaderUiOptionsPresetNames,
    rollbackUiOptionsData?: IVanillaReaderUiOptionsData,
    rollbackVanillaReaderViewOptionsData?: IVanillaReaderViewOptions,
}

export interface IVanillaReaderUiDialogResult<T> {
    title: string;
    data: T | undefined;
}

export type VanillaUiNavigationIntentEventCallback = (navigationData: IVanillaReaderAppEvent_NAVIGATION_INTENT) => void;
export type VanillaUiActionEventCallback = (actionName: VanillaReaderUiActionNames) => void;
export type VanillaUiSettingsUpdatedCallback = (settings: IVanillaReaderUiOptionsData) => void;
export type VanillaUiThemeChangedCallback = (palette: IVanillaReaderUiColorTheme) => void;
export type VanillaUiSliderValueChangedCallback = (palette: IVanillaReaderUiColorTheme) => void;
export type VanillaUiUnloadEventCallback = (settings: IVanillaReaderUiOptionsData) => void;
export type VanillaUiDialogResultCallback<T> = (result: IVanillaReaderUiDialogResult<T>) => void;
